// ============================================================================
// GLOBAL ACTIVITY FEED REPOSITORY
// ARQUIVO: src/pages/GlobalActivityFeedRepository.js
// RESPONSABILIDADE: Isolar todas as chamadas Firestore do GlobalActivityFeed.
// ============================================================================

import {
  arrayUnion, collectionGroup, doc, getDoc, limit, onSnapshot, orderBy, query, updateDoc
} from 'firebase/firestore';
import { db } from '../shared/api/firebase/config';

export const subscribeActivityFeed = (callback, onError) => {
  const ref = collectionGroup(db, 'notes');
  const qOrdered = query(ref, orderBy('createdAt', 'desc'), limit(100));
  const qFallback = query(ref, limit(250));

  let unsubscribe = null;

  const extractIndexUrl = (msg) => {
    const urlMatch = String(msg || '').match(/https:\/\/console\.firebase\.google\.com\/[^\s)"]+/i);
    return urlMatch ? String(urlMatch[0] || '') : '';
  };

  const isIndexError = (msg) => {
    const m = String(msg || '').toLowerCase();
    return m.includes('requires an index') || m.includes('create it here') || m.includes('failed-precondition');
  };

  const attach = (q, { fallbackMode }) => {
    if (typeof unsubscribe === 'function') {
      try { unsubscribe(); } catch (e) { void e; }
    }

    unsubscribe = onSnapshot(
      q,
      (snap) => {
        let out = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}), __ref: d.ref }));
        if (fallbackMode) {
          out = out.slice().sort((a, b) => {
            const av = a?.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
            const bv = b?.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
            return bv - av;
          }).slice(0, 100);
        }
        callback(out);
      },
      (e) => {
        const msg = String(e?.message || e || 'Erro ao carregar feed');
        const url = extractIndexUrl(msg);
        if (!fallbackMode && isIndexError(msg)) {
          onError({ type: 'index', url, msg });
          attach(qFallback, { fallbackMode: true });
          return;
        }
        onError({ type: 'error', url, msg });
      }
    );
  };

  attach(qOrdered, { fallbackMode: false });

  return () => {
    try { unsubscribe && unsubscribe(); } catch (e) { void e; }
  };
};

export const toggleEventPinned = async (ref, nextPinned) => {
  await updateDoc(ref, { isPinned: nextPinned });
};

export const addEventReply = async (ref, reply) => {
  await updateDoc(ref, { replies: arrayUnion(reply) });
};

export const fetchDocumentMeta = async (appId, documentId) => {
  const ref = doc(db, 'artifacts', appId, 'public', 'data', 'documents', documentId);
  const snap = await getDoc(ref);
  const data = snap?.data ? snap.data() : null;
  if (!data) return null;
  return {
    documentId,
    clientId: String(data?.clientId || '').trim(),
    title: String(data?.title || data?.name || data?.fileName || '').trim(),
    fileUrl: String(data?.fileUrl || data?.url || data?.downloadUrl || '').trim(),
    url: String(data?.url || '').trim(),
    downloadUrl: String(data?.downloadUrl || '').trim(),
    mirror_url: String(data?.mirror_url || '').trim(),
    storagePath: String(data?.storagePath || '').trim(),
    storage_path: String(data?.storage_path || data?._metadata?.storage_path || '').trim(),
    storageEngine: data?.storageEngine || null,
    publicationSeriesId: String(data?.publicationSeriesId || data?.id || documentId).trim(),
    publicationVersion: Math.max(0, Number(data?.publicationVersion || 0) || 0),
    requiresConsent: Boolean(data?.requiresConsent),
    consentStatus: String(data?.consentStatus || '').trim(),
    status: String(data?.status || '').trim(),
  };
};

export const fetchClientMeta = async (appId, clientId) => {
  const ref = doc(db, 'artifacts', appId, 'public', 'data', 'clients', clientId);
  const snap = await getDoc(ref);
  const data = snap?.data ? snap.data() : null;
  if (!data) return null;
  const name =
    data?.qualification?.physical?.name ||
    data?.qualification?.legal?.companyName ||
    data?.anchor?.name ||
    data?.name ||
    '';
  return String(name || '').trim() || null;
};
